/*=== CORE IMPORTS ===*/
import { html } from 'lit';
import { BaseLogItemDetailView } from './base-log-item-detail-view.js';
import { logItemViewStyles } from './log-item-view-styles.js';
import { updateLogItemStatus } from '../../modules/log-item-helpers-client.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/breadcrumb-item/breadcrumb-item.js';
import '@shoelace-style/shoelace/dist/components/breadcrumb/breadcrumb.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/color-picker/color-picker.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/radio-button/radio-button.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/relative-time/relative-time.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/*=== Q1 COMPONENTS ===*/
import '../../components/q1-markdown/q1-markdown.js';
import '../../components/q1-member-chip/q1-member-chip.js';

/*=== CONSTANTS ===*/

import {
  logItemStatusActions,
  logItemStatusActionsMap,
} from '../../models/log-item-client-actions.js'

/*=== VIEW CLASS DEFINITION ===*/

export class LogItemView extends BaseLogItemDetailView {
  static styles = [
    ...super.styles,
    logItemViewStyles,
  ];

  /*=== LIFECYCLE & INITIALIZATION METHODS ===*/
  
  async logItemLoaded() {
    this.setViewMeta(`${this.logItem?.headline} - ${this.subject?.name} - q1 Network`);
  }

  /*=== ACTIONS & EVENT HANDLERS ===*/

  openInSlack(event) {
    event.preventDefault();
    this.showToast('This feature is not implemented yet.', null, 'info');
  }

  handleAction(event) {
    event.preventDefault();

    // Get the item value, supporting either a button click or an sl-menu event
    const itemValue = event.detail?.item?.value || event.target?.value;
    const actionSettings = logItemStatusActionsMap[itemValue];
    const confirmationMessage = actionSettings?.confirmationMessage;
    const availableStati = actionSettings?.availableStati;

    // ES1: Action isn't properly configured
    if (!itemValue || !actionSettings) {
      console.error(`INVALID action value: ${itemValue}`);
      return null;
    }

    // ES2: Action not available for current status
    if (!availableStati || !availableStati.includes(this.logItem?.status)) {
      console.error(`Action not available for current status: ${this.logItem?.status}`, itemValue);
      return null;
    }

    // Error states are clear. Confirm with member if needed.
    if (confirmationMessage && !confirm(confirmationMessage)) {
      console.log(`Action cancelled by member: ${itemValue}`);
      return null;
    }

    // Now we're ready to perform the action
    switch (itemValue) {
      case 'edit':
        this.appElement.navigateTo(`/li/${this.logItemId}/edit`);
        break;

      case 'publish':
      case 'unarchive':
        this.loading = true;
        updateLogItemStatus(this.logItemId, 'published', this.showToast).finally(() => {
          this._refreshLogItem(); // will clear loading state
        });
        break;

      case 'archive':
        this.loading = true;
        updateLogItemStatus(this.logItemId, 'archived', this.showToast).finally(() => {
          this._refreshLogItem(); // will clear loading state
        });
        break;
      
      default:
        console.error(`UNHANDLED action value: ${itemValue}`);
    }
  }

  /*=== GETTERS ===*/

  get actionButtons() {
    if (!this.context?.canEdit) return [];

    return logItemStatusActions
      .filter(({ availableStati }) => availableStati.includes(this.logItem?.status));
  }

  get metaContext() {
    return {logItem: this.logItem};
  }

  /*=== RENDER TEMPLATES ===*/

  get contentHeaderTemplate() {
    if (['init'].includes(this.viewMode)) return null;

    return html`
      <header-bar class="small">
        ${this.subject && this.logItem ? html`

          <a
            href=${this.subject.detailViewPath}
            class="avatar-link"
            alt=${this.subject.name}
          >
            <sl-avatar
              image=${this.subject.avatar?.small?.url}
              label=${this.subject.name}
              shape=${this.subject.avatar_shape}
            >
              <sl-icon slot="icon" library="material" name=${this.subject.avatar_icon}></sl-icon>
            </sl-avatar>
          </a>
          
          <h1>
            <a class="prefix" href=${this.subject.detailViewPath}>${this.subject.name}</a>
            ${this.logItem.subjectCollection_titleCaseSingular} Log Update
          </h1>

          <button-bar class="sl-themed-secondary">
            ${this.actionButtonsTemplate}
          </button-bar>

        ` : html`

            <h1>
              <span class="prefix">Log Item View</span>
              Log Item Id: ${this.logItemId}
            </h1>

        `}
      </header-bar>
    `;
  }

  get viewModeTemplate_loaded() {
    return html`
      ${!this.error ? '' : html`
        <sl-alert variant="danger" open>
          <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
          ${this.errorMessage}
        </sl-alert>
      `}

      <log-item-content class="sl-themed-secondary">
        ${this.logItem.status === 'published' ? null : html`
          <sl-tooltip content=${this.logItem.status_description}>
            <sl-tag
              size="large"
              variant="primary"
              class="log-item-status ${this.logItem.status}"
              pill
            >
              <sl-icon
                library=${this.logItem.status_iconLibrary}
                name=${this.logItem.status_icon}
              ></sl-icon>
              ${this.logItem.status_label}
            </sl-tag>
          </sl-tooltip>
        `}

        <h1 class="main-header">${this.logItem.headline}</h1>

        <ul id="authors-panel">
          ${this.authors.map((author) => html`
            <li>
              <q1-member-chip
                .member=${author}
                ?signed-in=${this.isSignedIn}
              ></q1-member-chip>
            </li>
          `)}
        </ul>
        
        <div id="details-wrapper">
          <ul id="details-panel">
            <li>
              <sl-tooltip>
                <div slot="content">
                  ${this.logItem.status_timestampLabel}
                  <sl-relative-time .date=${this.logItem.status_timestamp}></sl-relative-time>
                </div>

                <sl-icon library="material" name="calendar_month"></sl-icon>
                <span>
                  ${this.logItem.status_timestampLabel}
                  <sl-format-date
                    date=${this.logItem.status_timestamp}
                    month="short" day="numeric" year="numeric"
                  ></sl-format-date>
                </span>
              </sl-tooltip>
            </li>

            <li>
              <sl-tooltip content=${this.logItem.visibility_description}>
                <sl-icon
                  library=${this.logItem.visibility_iconLibrary}
                  name=${this.logItem.visibility_icon}
                ></sl-icon>
                <span>${this.logItem.visibility_label}</span>
              </sl-tooltip>
            </li>

            ${this.logItem.status !== 'published' || !this.logItem.editCount ? null : html`
              <li>
                <sl-tooltip>
                  <div slot="content">
                    Last edited
                    <sl-format-date
                      date=${this.logItem.editedAt}
                      month="numeric" day="numeric" year="numeric"
                    ></sl-format-date>
                  </div>

                  <sl-icon library="material" name="update"></sl-icon>
                  <span>${this.logItem.editCount_text}</span>
                </sl-tooltip>
              </li>
            `}

            <li>
              <sl-icon
                library="material"
                name="schedule"
              ></sl-icon>
              <span>
                ${this.logItem.readingTime_text}
              </span>
            </li>
          </ul>
          <div id="details-actions">
            <a
              href="#"
              @click=${this.openInSlack}
            >
              <sl-icon slot="prefix" library="material" name="comment"></sl-icon>
              Discuss
              <span class="hide-until-bp2">in Slack</span>
            </a>
          </div>
        </div>
        
        <q1-markdown .content=${this.logItem.body}></q1-markdown>
      </log-item-content>
    `;
  }

  get actionButtonsTemplate() {
    return this.actionButtons.map(({ label, icon, iconLibrary, value, isPrimary }) => html`
      <sl-button
        variant=${isPrimary ? 'primary' : 'default'}
        @click=${this.handleAction}
        value=${value}
        ?disabled=${this.loading}
      >
        ${this.loading ? html`
          <sl-spinner slot="prefix"></sl-spinner>
          Processing...
        ` : html`
          <sl-icon slot="prefix" library=${iconLibrary} name=${icon}></sl-icon>
          ${label}
        `}
      </sl-button>
    `);
  }
  
}

customElements.define('log-item-view', LogItemView);
